import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
    PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";
import {
    DollarSign, Users, Building2, ShieldCheck, Activity, AlertTriangle,
    TrendingUp, MapPin, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";

export const ANALYTICS_TIDS = {
    root: "admin-analytics-root",
    refreshBtn: "admin-analytics-refresh",
    kpiGmv: "admin-analytics-kpi-gmv",
    kpiUsers: "admin-analytics-kpi-users",
    kpiCompliance: "admin-analytics-kpi-compliance",
    kpiAgencies: "admin-analytics-kpi-agencies",
    signupChart: "admin-analytics-signup-chart",
    statusChart: "admin-analytics-status-chart",
    stateBar: "admin-analytics-state-bar",
    incidentBar: "admin-analytics-incident-bar",
};

// Chart palette — muted, print-friendly, avoids AI-slop rainbow gradients
const PALETTE = {
    emerald: "#047857",
    teal:    "#0f766e",
    amber:   "#b45309",
    coral:   "#c2410c",
    slate:   "#475569",
    stone:   "#78716c",
    sky:     "#0369a1",
    rose:    "#be123c",
};
const STATUS_COLORS = {
    open: PALETTE.amber,
    matched: PALETTE.sky,
    in_progress: PALETTE.emerald,
    completed: PALETTE.teal,
    cancelled: PALETTE.rose,
    unknown: PALETTE.stone,
};
const SEVERITY_COLORS = {
    low: PALETTE.stone,
    medium: PALETTE.amber,
    high: PALETTE.coral,
    critical: PALETTE.rose,
};

function KpiCard({ testId, icon: Icon, label, value, sub, accent = "emerald" }) {
    const bg = { emerald: "bg-emerald-50", amber: "bg-amber-50", sky: "bg-sky-50", stone: "bg-stone-50" }[accent];
    const fg = { emerald: "text-emerald-700", amber: "text-amber-700", sky: "text-sky-700", stone: "text-stone-700" }[accent];
    return (
        <div data-testid={testId} className={`rounded-2xl p-5 border border-stone-200 ${bg}`}>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                <Icon className={`w-3.5 h-3.5 ${fg}`} />
                {label}
            </div>
            <div className="font-outfit text-3xl font-semibold text-stone-900">{value}</div>
            {sub && <div className="text-xs text-stone-500 font-dmsans mt-1">{sub}</div>}
        </div>
    );
}

export default function AdminAnalyticsPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/analytics");
            setData(data);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    if (loading && !data) {
        return (
            <Card className="rounded-2xl border-stone-200">
                <CardContent className="p-10 text-center text-sm text-stone-500 font-dmsans">
                    Crunching numbers…
                </CardContent>
            </Card>
        );
    }
    if (!data) return null;

    const gmv = data.care_requests.monthly_gmv_cents || 0;
    const weekly = data.care_requests.weekly_gmv_cents || 0;
    const financial = !!data.financial_visible;
    const statusData = Object.entries(data.care_requests.by_status || {}).map(([status, v]) => ({
        status, count: v.count,
    }));
    const stateData = (data.care_requests.top_states || []).map((s) => ({
        state: s.state,
        weekly: (s.weekly_cents || 0) / 100,
        count: s.count,
    }));
    const incidentData = Object.entries(data.incidents_30d || {}).map(([sev, count]) => ({
        severity: sev, count, fill: SEVERITY_COLORS[sev] || PALETTE.stone,
    }));
    const failedLogins = data.security?.failed_logins_24h || 0;
    const compliancePct = data.caregivers?.fully_verified_pct || 0;

    return (
        <div data-testid={ANALYTICS_TIDS.root} className="space-y-5">
            {/* Header row */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-outfit text-2xl font-semibold text-stone-900">
                        Platform analytics
                    </h2>
                    <div className="text-xs text-stone-500 font-dmsans mt-0.5">
                        Rolled up {new Date(data.generated_at).toLocaleString()}
                    </div>
                </div>
                <button
                    data-testid={ANALYTICS_TIDS.refreshBtn}
                    onClick={load}
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-outfit font-medium bg-stone-100 hover:bg-stone-200 text-stone-800"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {financial ? (
                    <KpiCard
                        testId={ANALYTICS_TIDS.kpiGmv}
                        icon={DollarSign}
                        label="Monthly GMV (est.)"
                        value={`$${(gmv / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                        sub={`$${(weekly / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} / wk in matched+active care`}
                        accent="emerald"
                    />
                ) : (
                    <KpiCard
                        icon={ShieldCheck}
                        label="Financials"
                        value="Hidden"
                        sub="Executive view only"
                        accent="stone"
                    />
                )}                <KpiCard
                    testId={ANALYTICS_TIDS.kpiUsers}
                    icon={Users}
                    label="Total users"
                    value={data.total_users}
                    sub={Object.entries(data.users_by_role || {}).map(([r, c]) => `${c} ${r}`).join(" · ")}
                    accent="sky"
                />
                <KpiCard
                    testId={ANALYTICS_TIDS.kpiCompliance}
                    icon={ShieldCheck}
                    label="Caregivers fully verified"
                    value={`${compliancePct}%`}
                    sub={`${data.caregivers?.background_check_verified || 0} BG · ${data.caregivers?.cpr_verified || 0} CPR · ${data.caregivers?.insurance_verified || 0} Ins`}
                    accent={compliancePct >= 80 ? "emerald" : compliancePct >= 50 ? "amber" : "stone"}
                />
                <KpiCard
                    testId={ANALYTICS_TIDS.kpiAgencies}
                    icon={Building2}
                    label="Agencies on Stripe"
                    value={`${data.agencies?.onboarded_stripe || 0}/${data.agencies?.total || 0}`}
                    sub={`${data.agencies?.onboarded_pct || 0}% onboarded`}
                    accent={data.agencies?.onboarded_pct >= 80 ? "emerald" : "amber"}
                />
            </div>

            {/* Secondary KPIs strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="rounded-xl bg-white border border-stone-200 p-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-emerald-700" />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Active visits</div>
                        <div className="font-outfit text-lg font-semibold">{data.visits?.active || 0}</div>
                    </div>
                </div>
                <div className="rounded-xl bg-white border border-stone-200 p-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-700" />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Visits · 30d</div>
                        <div className="font-outfit text-lg font-semibold">{data.visits?.completed_30d || 0}</div>
                    </div>
                </div>
                <div className="rounded-xl bg-white border border-stone-200 p-3 flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${failedLogins > 20 ? "text-red-600" : "text-stone-500"}`} />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Failed logins · 24h</div>
                        <div className={`font-outfit text-lg font-semibold ${failedLogins > 20 ? "text-red-700" : "text-stone-900"}`}>
                            {failedLogins}
                        </div>
                    </div>
                </div>
                <div className="rounded-xl bg-white border border-stone-200 p-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-emerald-700" />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Package vs à-la-carte</div>
                        <div className="font-outfit text-lg font-semibold">
                            {data.care_requests?.package_vs_alacarte?.package || 0}/
                            {data.care_requests?.package_vs_alacarte?.alacarte || 0}
                        </div>
                    </div>
                </div>
            </div>

            {/* Signup timeseries — full width */}
            <Card className="rounded-2xl border-stone-200">
                <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h3 className="font-outfit text-lg font-semibold text-stone-900">
                                Signups · last 30 days
                            </h3>
                            <div className="text-xs text-stone-500 font-dmsans">by role, daily</div>
                        </div>
                    </div>
                    <div data-testid={ANALYTICS_TIDS.signupChart} className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.signups_timeseries || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#78716c" }} />
                                <YAxis tick={{ fontSize: 10, fill: "#78716c" }} allowDecimals={false} />
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7e5e4" }} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Line type="monotone" dataKey="family" stroke={PALETTE.emerald} strokeWidth={2} dot={false} />
                                <Line type="monotone" dataKey="caregiver" stroke={PALETTE.sky} strokeWidth={2} dot={false} />
                                <Line type="monotone" dataKey="agency" stroke={PALETTE.amber} strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Care request status + Top states */}
            <div className="grid md:grid-cols-2 gap-3">
                <Card className="rounded-2xl border-stone-200">
                    <CardContent className="p-5">
                        <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-3">
                            Care requests by status
                        </h3>
                        <div data-testid={ANALYTICS_TIDS.statusChart} className="h-56">
                            {statusData.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-xs text-stone-400 font-dmsans">
                                    No care requests yet
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={statusData}
                                            dataKey="count"
                                            nameKey="status"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={80}
                                            innerRadius={40}
                                            paddingAngle={2}
                                        >
                                            {statusData.map((d) => (
                                                <Cell key={d.status} fill={STATUS_COLORS[d.status] || PALETTE.stone} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ fontSize: 12 }} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-2xl border-stone-200">
                    <CardContent className="p-5">
                        <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-emerald-700" />
                            {financial ? "Top states by weekly GMV" : "Top states by request volume"}
                        </h3>
                        <div className="text-xs text-stone-500 font-dmsans mb-3">
                            {financial ? "USD / week" : "care requests count"}
                        </div>
                        <div data-testid={ANALYTICS_TIDS.stateBar} className="h-56">
                            {stateData.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-xs text-stone-400 font-dmsans">
                                    No state data yet
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stateData} layout="vertical" margin={{ left: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" horizontal={false} />
                                        <XAxis type="number" tick={{ fontSize: 10, fill: "#78716c" }} />
                                        <YAxis
                                            type="category"
                                            dataKey="state"
                                            width={40}
                                            tick={{ fontSize: 11, fill: "#1c1917", fontWeight: 600 }}
                                        />
                                        <Tooltip
                                            contentStyle={{ fontSize: 12, borderRadius: 8 }}
                                            formatter={(v) =>
                                                financial
                                                    ? [`$${Number(v).toLocaleString()}`, "weekly"]
                                                    : [Number(v).toLocaleString(), "requests"]
                                            }
                                        />
                                        <Bar
                                            dataKey={financial ? "weekly" : "count"}
                                            fill={PALETTE.emerald}
                                            radius={[0, 6, 6, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Incidents */}
            <Card className="rounded-2xl border-stone-200">
                <CardContent className="p-5">
                    <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-700" /> Incidents · 30 days by severity
                    </h3>
                    <div className="text-xs text-stone-500 font-dmsans mb-3">
                        {incidentData.length === 0 ? "No incidents reported in the last 30 days." : "count per severity"}
                    </div>
                    {incidentData.length > 0 && (
                        <div data-testid={ANALYTICS_TIDS.incidentBar} className="h-48">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={incidentData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                    <XAxis dataKey="severity" tick={{ fontSize: 11, fill: "#78716c" }} />
                                    <YAxis tick={{ fontSize: 10, fill: "#78716c" }} allowDecimals={false} />
                                    <Tooltip contentStyle={{ fontSize: 12 }} />
                                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                        {incidentData.map((d) => (
                                            <Cell key={d.severity} fill={d.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
